import { useRef, useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import type { SkipReason } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Eyebrow, Pill, Progress, Skeleton, useToast } from '../../ds/components';
import { useIsMobile } from '../../ds/hooks';
import { errorMessage } from '../../lib/api';
import { OutcomeForm } from '../appointments/OutcomeDialog';
import { bookAppointmentHref, isNextStepRequired, useTask, type TaskItem } from '../tasks/api';
import { shortDay } from '../tasks/dates';
import { NextStepDialog, type NextStep } from '../tasks/NextStepDialog';
import { SnoozePopover, type SnoozeValue } from '../tasks/SnoozePopover';
import { useTasks } from '../tasks/api';
import {
  entryTitle,
  useAdvanceQueue,
  useCurrentQueue,
  useEndQueue,
  usePauseQueue,
  type QueueState,
} from './api';
import { ContactCard, queueContactRows } from './ContactCard';
import { QueueSummary } from './QueueSummary';
import { SkipDialog } from './SkipDialog';
import styles from './QueueSessionPage.module.css';

/** „Unterlagen nachfassen · fällig seit 23.09." / „… · fällig heute, 14:00" */
function taskSubtitle(task: TaskItem): string {
  const due =
    task.due.overdue && task.due.on ? `fällig seit ${shortDay(task.due.on)}` : `fällig ${task.due.label}`;
  return `${task.title} · ${due}`;
}

function taskMeta(task: TaskItem): string {
  const due =
    task.due.overdue && task.due.on ? `Fällig seit ${shortDay(task.due.on)}` : `Fällig ${task.due.label}`;
  return task.reference ? `${due} · ${task.reference}` : due;
}

interface FocusBarProps {
  state: QueueState;
  paused: boolean;
  busy: boolean;
  canSkip: boolean;
  onSkip: () => void;
  onPause: () => void;
  onEnd: () => void;
}

/** The queue bar of the focus mode (Queue 03–07, variant B): source, position, progress, three actions. */
function FocusBar({ state, paused, busy, canSkip, onSkip, onPause, onEnd }: FocusBarProps) {
  const mobile = useIsMobile();
  const session = state.session;
  if (!session) return null;
  const position = `${state.progress.position} von ${state.progress.total}`;
  return (
    <div className={clsx(styles.bar, mobile && styles.barMobile)} role="region" aria-label="Queue">
      <div className={styles.barMain}>
        <span className={styles.barEyebrow}>Queue</span>
        {mobile ? (
          <span className={styles.barSource}>Task {position}</span>
        ) : (
          <>
            <span className={styles.barSource}>{session.sourceLabel}</span>
            <span className={styles.barPosition}>{position}</span>
          </>
        )}
        {paused ? <span className={styles.barPaused}>pausiert</span> : null}
        {mobile ? null : (
          <Progress
            value={state.progress.percent}
            label="Fortschritt der Queue"
            variant="inverse"
            className={styles.barProgress}
          />
        )}
        <span className={styles.barActions}>
          {canSkip ? (
            <Button
              variant="ghostInverse"
              size="small"
              className={styles.barButton}
              disabled={busy || paused}
              onClick={onSkip}
            >
              Überspringen
            </Button>
          ) : null}
          <Button
            variant="ghostInverse"
            size="small"
            className={styles.barButton}
            disabled={busy}
            onClick={onPause}
          >
            {paused ? 'Fortsetzen' : mobile ? 'Pause' : 'Pausieren'}
          </Button>
          <Button
            variant="ghostInverse"
            size="small"
            className={styles.barButton}
            disabled={busy}
            onClick={onEnd}
          >
            Beenden
          </Button>
        </span>
      </div>
      {mobile ? (
        <Progress
          value={state.progress.percent}
          label="Fortschritt der Queue"
          variant="inverse"
          className={styles.barProgressMobile}
        />
      ) : null}
    </div>
  );
}

/**
 * `/tasks/queue/session` — the focus mode (Queue 03–07): queue bar, the current entry as task card or
 * outcome form with the contact card on the right, skip / pause / end, and the summary (Queue 10/11)
 * after „Beenden" (`?summary=<id>` survives a reload).
 */
export function QueueSessionPage() {
  const [params] = useSearchParams();
  const summaryId = params.get('summary');
  if (summaryId !== null) return <QueueSummary sessionId={summaryId} />;
  return <FocusMode />;
}

/** Topbar (60) + queue bar (44): the scrim of the focus-mode dialogs starts below the bar (Queue 03/07). */
const FOCUS_OVERLAY_TOP = 104;

function FocusMode() {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const { toast } = useToast();
  const current = useCurrentQueue();
  const tasks = useTasks(false);
  const advance = useAdvanceQueue();
  const pause = usePauseQueue();
  const end = useEndQueue();
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipError, setSkipError] = useState<string | null>(null);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [nextStepOpen, setNextStepOpen] = useState(false);
  const [nextStepError, setNextStepError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const snoozeTrigger = useRef<HTMLButtonElement>(null);

  const state = current.data;
  const entry = state?.entry ?? null;
  const task = entry?.task ?? null;
  const detail = useTask(task ? task.id : null);
  const quickPicks = tasks.data?.quickPicks ?? [];

  if (current.isError) {
    return (
      <div className={styles.content}>
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void current.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(current.error)}
        </Banner>
      </div>
    );
  }
  if (current.isPending) {
    return (
      <div aria-busy="true" aria-label="Wird geladen">
        <div className={styles.bar}>
          <div className={styles.barMain}>
            <Skeleton width={180} height={8} />
          </div>
        </div>
        <div className={styles.content}>
          <Skeleton width="40%" height={26} />
          <div className={styles.grid}>
            <Skeleton variant="block" height={180} />
            <Skeleton variant="block" height={220} />
          </div>
        </div>
      </div>
    );
  }
  if (!state || state.session === null) {
    // „Beenden" clears the session before the summary route is reached — no redirect in between.
    if (end.isPending || end.isSuccess) return null;
    return <Navigate to="/tasks/queue" replace />;
  }

  const session = state.session;
  const paused = session.status === 'paused';
  const busy = advance.isPending || pause.isPending || end.isPending;

  async function runAdvance(body: Parameters<typeof advance.mutateAsync>[0]['body']): Promise<boolean> {
    setActionError(null);
    try {
      const result = await advance.mutateAsync({ id: session.id, body });
      if (result.next === 'book_appointment' && task?.contactId) {
        void navigate(bookAppointmentHref(task.contactId, result.createdTaskId));
      }
      if (result.skip?.stuck) toast({ message: 'Task an „Hängt fest“ übergeben' });
      return true;
    } catch (error) {
      if (
        body.result === 'done' &&
        isNextStepRequired(error) &&
        body.taskCompletion?.nextStep === undefined
      ) {
        setNextStepError(null);
        setNextStepOpen(true);
        return false;
      }
      throw error;
    }
  }

  function completeEntry(nextStep?: NextStep) {
    runAdvance({ result: 'done', ...(nextStep ? { taskCompletion: { nextStep } } : {}) })
      .then((ok) => {
        if (ok) {
          setNextStepOpen(false);
          setNextStepError(null);
        }
      })
      .catch((error: unknown) => {
        if (nextStep) setNextStepError(errorMessage(error));
        else setActionError(errorMessage(error));
      });
  }

  function skipEntry(reason: SkipReason, note: string | null) {
    setSkipError(null);
    runAdvance({ result: 'skipped', skip: { reason, note } })
      .then(() => setSkipOpen(false))
      .catch((error: unknown) => setSkipError(errorMessage(error)));
  }

  function laterEntry(value: SnoozeValue) {
    runAdvance({ result: 'later', later: value })
      .then(() => {
        setSnoozeOpen(false);
        setSkipOpen(false);
      })
      .catch((error: unknown) => {
        setSkipError(errorMessage(error));
        setActionError(errorMessage(error));
      });
  }

  function togglePause() {
    pause.mutate(
      { id: session.id, action: paused ? 'resume' : 'pause' },
      { onError: (error) => setActionError(errorMessage(error)) },
    );
  }

  function endSession() {
    end
      .mutateAsync(session.id)
      .then((summary) => navigate(`/tasks/queue/session?summary=${summary.sessionId}`))
      .catch((error: unknown) => setActionError(errorMessage(error)));
  }

  let title = '';
  let subtitle = '';
  let pill = '';
  let left: ReactNode = null;
  if (entry === null) {
    left = (
      <div className={styles.card}>
        <Eyebrow>Queue</Eyebrow>
        <div className={styles.cardText}>Die Queue ist abgearbeitet.</div>
        <div className={styles.cardMeta}>
          Alle Einträge dieser Session sind bearbeitet — beenden Sie die Session für die Zusammenfassung.
        </div>
        <div className={styles.cardActions}>
          <Button onClick={endSession} loading={end.isPending}>
            Beenden
          </Button>
        </div>
      </div>
    );
  } else if (entry.kind === 'appointment' && entry.appointment) {
    const appointment = entry.appointment;
    title = entryTitle(entry);
    subtitle = `${appointment.eventTypeName} · Ergebnis fehlt`;
    pill = 'Termin-Ergebnis';
    left = (
      <OutcomeForm
        card
        appointmentId={appointment.id}
        contactId={entry.contact?.id ?? null}
        contactName={entry.contact?.name ?? null}
        eventTypeName={appointment.eventTypeName}
        subtitle={appointment.label}
        variant="queue"
        onDone={() => completeEntry()}
        onReschedule={(id) => void navigate(`/calendar/appointments/${id}?reschedule=1`)}
      />
    );
  } else if (task) {
    title = entryTitle(entry);
    subtitle = taskSubtitle(task);
    pill = task.typeLabel;
    const wrapUp = task.type === 'appointment_outcome' && task.appointmentId !== null;
    left = wrapUp ? (
      <OutcomeForm
        card
        appointmentId={task.appointmentId ?? ''}
        contactId={task.contactId}
        contactName={task.contact?.name ?? null}
        eventTypeName={task.reference}
        subtitle={task.reference}
        variant="queue"
        onDone={() => completeEntry()}
        onReschedule={(id) => void navigate(`/calendar/appointments/${id}?reschedule=1`)}
      />
    ) : (
      <div className={styles.card}>
        <Eyebrow>Aufgabe</Eyebrow>
        <div className={styles.cardText}>{task.title}</div>
        <div className={styles.cardMeta}>{taskMeta(task)}</div>
        <div className={styles.cardActions}>
          {task.href.startsWith('/tasks?task=') ? null : (
            <Button onClick={() => void navigate(task.href)} disabled={paused}>
              {task.cta}
            </Button>
          )}
          <span className={styles.snoozeAnchor}>
            <Button
              ref={snoozeTrigger}
              variant="secondary"
              aria-haspopup="dialog"
              aria-expanded={snoozeOpen}
              disabled={busy || paused}
              onClick={() => setSnoozeOpen((value) => !value)}
            >
              Später
            </Button>
            {snoozeOpen ? (
              <SnoozePopover
                placement="end"
                quickPicks={quickPicks}
                initialOn={task.due.on}
                initialTime={task.due.time}
                pending={advance.isPending}
                error={actionError}
                triggerRef={snoozeTrigger}
                onCancel={() => setSnoozeOpen(false)}
                onSubmit={laterEntry}
              />
            ) : null}
          </span>
          <Button variant="secondary" disabled={busy || paused} onClick={() => completeEntry()}>
            Erledigt
          </Button>
        </div>
      </div>
    );
  } else if (entry.contact) {
    title = entry.contact.name;
    subtitle = entry.contact.roleLine;
    pill = 'Kontakt';
    left = (
      <div className={styles.card}>
        <Eyebrow>Kontakt</Eyebrow>
        <div className={styles.cardText}>{entry.contact.name}</div>
        <div className={styles.cardMeta}>Kontakte aus Dialer-Listen werden mit dem Dialer bearbeitet.</div>
        <div className={styles.cardActions}>
          <Button onClick={() => void navigate(`/contacts/${entry.contact?.id ?? ''}`)}>Öffnen</Button>
          <Button variant="secondary" disabled={busy || paused} onClick={() => completeEntry()}>
            Erledigt
          </Button>
        </div>
      </div>
    );
  }

  const detailData = detail.data;
  const showDetails =
    detailData !== undefined &&
    task !== null &&
    (detailData.description !== null || detailData.skips.length > 0 || detailData.attachments.length > 0);

  return (
    <div className={styles.page}>
      <FocusBar
        state={state}
        paused={paused}
        busy={busy}
        canSkip={entry !== null}
        onSkip={() => {
          setSkipError(null);
          setSkipOpen(true);
        }}
        onPause={togglePause}
        onEnd={endSession}
      />
      <div className={styles.content}>
        {paused ? (
          <Banner
            tone="accent"
            actions={
              <Button size="small" onClick={togglePause} loading={pause.isPending}>
                Fortsetzen
              </Button>
            }
          >
            Die Queue ist pausiert. Nach 24 Stunden wird sie automatisch beendet.
          </Banner>
        ) : null}
        {actionError && !snoozeOpen ? <Banner tone="danger">{actionError}</Banner> : null}
        {entry !== null ? (
          <>
            <div className={styles.titleRow}>
              <Pill variant="accent">{pill}</Pill>
              <h1 className={styles.title}>{title}</h1>
            </div>
            <div className={styles.subtitle}>{subtitle}</div>
          </>
        ) : null}
        <div className={styles.grid}>
          <div className={styles.column}>
            {left}
            {showDetails && detailData ? (
              <div className={styles.card}>
                <Eyebrow>Details</Eyebrow>
                {detailData.description ? (
                  <p className={styles.description}>{detailData.description}</p>
                ) : null}
                {detailData.skips.length > 0 ? (
                  <ul className={styles.list}>
                    {detailData.skips.map((skip) => (
                      <li key={skip.at} className={styles.listItem}>
                        {skip.label}
                        {skip.note ? <span className={styles.listNote}>„{skip.note}“</span> : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {detailData.attachments.length > 0 ? (
                  <ul className={styles.list}>
                    {detailData.attachments.map((file) => (
                      <li key={file.id} className={styles.listItem}>
                        <a href={`/api/files/${file.fileId}`} target="_blank" rel="noreferrer">
                          {file.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className={styles.column}>
            {entry?.contact ? (
              <ContactCard
                name={entry.contact.name}
                initials={entry.contact.initials}
                roleLine={entry.contact.roleLine}
                rows={queueContactRows(entry.contact)}
                href={`/contacts/${entry.contact.id}`}
              />
            ) : null}
          </div>
        </div>
      </div>

      <SkipDialog
        open={skipOpen}
        task={task}
        quickPicks={quickPicks}
        pending={advance.isPending}
        error={skipError}
        onCancel={() => setSkipOpen(false)}
        onSkip={skipEntry}
        onLater={task ? laterEntry : null}
        overlayTop={mobile ? 0 : FOCUS_OVERLAY_TOP}
        className={mobile ? undefined : styles.focusDialog}
      />
      <NextStepDialog
        open={nextStepOpen}
        contactName={entry?.contact?.name ?? task?.contact?.name ?? task?.reference ?? ''}
        onBack={() => {
          setNextStepOpen(false);
          setNextStepError(null);
        }}
        onSubmit={(step) => completeEntry(step)}
        pending={advance.isPending}
        error={nextStepError}
        overlayTop={mobile ? 0 : FOCUS_OVERLAY_TOP}
        className={mobile ? undefined : styles.focusDialog}
      />
    </div>
  );
}
